import { useRef, useState } from 'react';
import { Upload, FileText, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { parse1040Text, type Parsed1040 } from '@/lib/parse1040';
import { isStaleChunkError, STALE_DEPLOY_MESSAGE } from '@/lib/staleDeploy';
import { formatCurrency } from '@/tax/format';
import { parseDollars } from '@/lib/parseDollars';
import type { TaxScenarioInput } from '@/tax/types';

interface Import1040DialogProps {
  /** Called with the parsed partial input when the user confirms the import. */
  onApply: (fields: Partial<TaxScenarioInput>) => void;
  /** The `import_wage_split` feature flag: ask for the W-2 wage split on the
   *  import screen. Off, the wages stay on the taxpayer and the Heads up box
   *  says what to enter by hand (off until Dylan has looked at it). */
  wageSplitBox?: boolean;
}

type Stage = 'idle' | 'extracting' | 'review' | 'error';

export default function Import1040Dialog({ onApply, wageSplitBox = false }: Import1040DialogProps) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>('idle');
  const [parsed, setParsed] = useState<Parsed1040 | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Joint return without the Schedule SE pages: the taxpayer's own W-2 wages,
  // typed here. Blank until the advisor enters it; the spouse gets the rest.
  const [tpWagesText, setTpWagesText] = useState('');
  // "I don't have the W-2s": import with all the wages on the taxpayer, as
  // before the split existed, and say so in the Heads up box.
  const [skipSplit, setSkipSplit] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStage('idle');
    setParsed(null);
    setErrorMsg(null);
    setTpWagesText('');
    setSkipSplit(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setErrorMsg('Please choose a .pdf file.');
      setStage('error');
      return;
    }
    setStage('extracting');
    setErrorMsg(null);
    try {
      const { extractPdfText } = await import('@/lib/extractPdfText');
      const text = await extractPdfText(file);
      const result = parse1040Text(text);
      setParsed(result);
      setStage('review');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to read PDF.';
      // Stale tab after a new deploy: the dynamic chunk hash in the cached
      // index.html no longer exists on the CDN. The global vite:preloadError
      // listener has already flagged the deploy watcher, so the header pill is
      // showing; this points at it. Don't show the raw fetch error to the user.
      setErrorMsg(isStaleChunkError(msg) ? STALE_DEPLOY_MESSAGE : msg);
      setStage('error');
    }
  };

  // Joint return with a business and no Schedule SE pages: the import can't
  // tell whose W-2 wages are whose. With the box, the advisor splits them
  // here; without it (flag off, or "I don't have the W-2s"), all of line 1a
  // stays on the taxpayer and the Heads up box says what to enter by hand.
  const splitTotal = parsed?.wageSplitTotal ?? null;
  const splitting = splitTotal !== null && wageSplitBox && !skipSplit;
  // Whole dollars, cents rounded — the same rule as every wizard money box.
  const tpWages = tpWagesText === '' ? null : parseDollars(tpWagesText);
  const splitInvalid = splitting && (tpWages === null || tpWages > splitTotal);
  const warnings = parsed
    ? (splitTotal !== null && !splitting
      ? [...parsed.warnings, `This joint return has a business on Schedule C but no Schedule SE pages, so all ${formatCurrency(splitTotal)} of W-2 wages are on the taxpayer and the self-employment tax may be off. Fix it by entering each spouse's W-2 wages on the Income step (Wages and Wages (Spouse)) and each business owner's W-2 box 3 on the Business step (W-2 SS Wages).`]
      : parsed.warnings)
    : [];

  const handleApply = () => {
    if (!parsed || splitInvalid) return;
    onApply(splitting && tpWages !== null
      ? { ...parsed.fields, wages: tpWages, spouseWages: splitTotal - tpWages }
      : parsed.fields);
    const count = parsed.extracted.filter(e => e.line !== 'header').length;
    toast.success(`Imported ${count} field${count === 1 ? '' : 's'} from 1040. Review the wizard steps before saving.`);
    setOpen(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="gap-2">
          <Upload className="h-4 w-4" />
          Import from 1040
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import from Form 1040 PDF</DialogTitle>
          <DialogDescription>
            Upload a tax-software-generated 1040 PDF (TurboTax, Drake, Lacerte, ProConnect, etc.).
            We'll pull the line items the engine uses and pre-fill the wizard. The PDF stays in your
            browser — nothing is uploaded.
          </DialogDescription>
        </DialogHeader>

        {stage === 'idle' && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
            >
              <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">Click to choose a PDF, or drop one here</p>
              <p className="text-xs text-muted-foreground mt-1">Native-text PDFs only — scans aren't supported yet.</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </div>
        )}

        {stage === 'extracting' && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Reading PDF…</p>
          </div>
        )}

        {stage === 'error' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not read PDF</AlertTitle>
            <AlertDescription>{errorMsg ?? 'Unknown error.'}</AlertDescription>
          </Alert>
        )}

        {stage === 'review' && parsed && (
          <div className="space-y-4">
            {parsed.likelyScanned && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Looks like a scanned PDF</AlertTitle>
                <AlertDescription>
                  We couldn't find a text layer. Re-export the return from your tax software as a PDF
                  (not a scan or photo), or enter the values manually.
                </AlertDescription>
              </Alert>
            )}
            {parsed.notRecognized && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Not a 1040</AlertTitle>
                <AlertDescription>This file does not look like a Form 1040.</AlertDescription>
              </Alert>
            )}

            {!parsed.likelyScanned && !parsed.notRecognized && (
              <>
                {warnings.length > 0 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Heads up</AlertTitle>
                    <AlertDescription>
                      <ul className="list-disc pl-5 space-y-1 text-xs mt-1">
                        {warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {splitTotal !== null && wageSplitBox && (
                  <div className="rounded-lg border p-3 space-y-3" data-testid="import-wage-split">
                    <div className="text-sm space-y-2">
                      <p className="font-medium">Split the W-2 wages between the two spouses</p>
                      <p className="text-muted-foreground">
                        This joint return shows {formatCurrency(splitTotal)} of W-2 wages, both spouses added together.
                        It also has a business on Schedule C, and self-employment tax is worked out per person:
                        Social Security tax stops once one person's wages and business income reach the yearly
                        Social Security wage cap, and that person's W-2 wages count toward the cap first. So the
                        app needs to know how much of the {formatCurrency(splitTotal)} is each spouse's. The
                        Schedule SE pages that show this weren't in the PDF.
                      </p>
                      <p className="text-muted-foreground">
                        <span className="font-medium text-foreground">Where to find it:</span> box 1 of the
                        taxpayer's own W-2s (add them together if there's more than one). The spouse gets the rest.
                      </p>
                      <p className="text-muted-foreground">
                        <span className="font-medium text-foreground">If this is guessed,</span> the
                        self-employment tax will come out wrong.
                      </p>
                    </div>
                    {skipSplit ? (
                      <div className="space-y-1">
                        <p className="text-sm">
                          Importing with all {formatCurrency(splitTotal)} of W-2 wages on the taxpayer. The Heads up
                          above says what to fix later.
                        </p>
                        <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => setSkipSplit(false)}>
                          Enter the split after all
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="import-tp-wages" className="text-xs text-muted-foreground">Taxpayer W-2 wages</Label>
                            <Input
                              id="import-tp-wages"
                              inputMode="decimal"
                              // A decimal in progress ("120000.") is shown as typed until the box is left.
                              value={tpWagesText === '' ? '' : /\.\d*$/.test(tpWagesText) ? tpWagesText : (tpWages ?? 0).toLocaleString('en-US')}
                              onChange={e => setTpWagesText(e.target.value)}
                              onBlur={() => setTpWagesText(t => (t === '' ? '' : String(parseDollars(t))))}
                              placeholder="Enter amount"
                              className="h-9 font-mono text-sm"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-muted-foreground">Spouse W-2 wages</Label>
                            <p className="h-9 flex items-center font-mono text-sm" data-testid="import-sp-wages">
                              {tpWages === null || tpWages > splitTotal ? '—' : formatCurrency(splitTotal - tpWages)}
                            </p>
                          </div>
                        </div>
                        {tpWages !== null && tpWages > splitTotal && (
                          <p className="text-xs text-muted-foreground">Can't be more than {formatCurrency(splitTotal)}.</p>
                        )}
                        <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => setSkipSplit(true)}>
                          I don't have the W-2s
                        </Button>
                      </>
                    )}
                  </div>
                )}

                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>
                    Found {parsed.extracted.filter(e => e.line !== 'header').length} field
                    {parsed.extracted.filter(e => e.line !== 'header').length === 1 ? '' : 's'}
                  </AlertTitle>
                  <AlertDescription>
                    Review what we picked up below. Anything not listed will need to be entered in the wizard.
                  </AlertDescription>
                </Alert>

                <div className="rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-left text-xs">
                      <tr>
                        <th className="px-3 py-2 font-medium">Line</th>
                        <th className="px-3 py-2 font-medium">Field</th>
                        <th className="px-3 py-2 font-medium text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.extracted.map((row, i) => (
                        <tr key={`${row.line}-${i}`} className="border-t">
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{row.line}</td>
                          <td className="px-3 py-2">{row.label}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {typeof row.value === 'number'
                              // Tax year is a number but should display as a plain
                              // 4-digit year, not currency-formatted.
                              ? (row.label === 'Tax Year' ? String(row.value) : formatCurrency(row.value))
                              : row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

              </>
            )}
          </div>
        )}

        <DialogFooter>
          {stage === 'review' && parsed && !parsed.likelyScanned && !parsed.notRecognized && (
            <>
              <Button variant="outline" onClick={reset}>
                Choose a different file
              </Button>
              <Button onClick={handleApply} disabled={splitInvalid}>
                Apply to wizard
              </Button>
            </>
          )}
          {(stage === 'error' || (stage === 'review' && parsed && (parsed.likelyScanned || parsed.notRecognized))) && (
            <Button variant="outline" onClick={reset}>
              Try again
            </Button>
          )}
          {stage === 'idle' && (
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
